import React from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';

const WorkingThroughConflictStage3 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Scenario 3: The dysfunctional leadership team" icon={<ChartBarIcon className="h-7 w-7 text-primary-light" />} heading>
      {/* TL;DR Section */}
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <p className="mb-4">You need to do 3 things:</p>
          <ol className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">1.</span>
              <span>Figure out what's really behind it…</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">2.</span>
              <span>Co-create a solution and identify your newspaper headline</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">3.</span>
              <span>Commit to a timeframe to work to fix it</span>
            </li>
          </ol>
          <p className="mt-4">Here's what we mean</p>
        </ExpandableCallout>
      </PlaybookCard>

      {/* Introduction */}
      <PlaybookCard>
        <p className="text-gray-300 mb-6">
          Some triggers for this:
        </p>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">Unproductive leadership team meetings</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">Verbal abuse between team members</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">Blame game</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">Not really talking about 'the thing'</span>
          </li>
        </ul>
      </PlaybookCard>

      {/* Diagnose the Underlying Issues */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Diagnose the Underlying Issues
        </h3>
        <p className="text-gray-300 mb-6">
          The first step is understanding what's driving the dysfunctional behaviours. This could range from personal conflicts and communication gaps to misaligned goals or even stress from external pressures.
        </p>
        <p className="text-gray-300 mb-6">
          You have to understand the power dynamics at play
        </p>

        <h4 className="text-lg font-semibold text-white mb-3">
          Some things you can do:
        </h4>
        <ol className="list-decimal pl-6 space-y-3 text-gray-300 mb-6">
          <li>
            <span className="font-medium text-white">Conduct confidential one-on-one interviews</span>: Speak individually with each leadership team member to gather their perspectives and insights on the current team dynamics.
          </li>
          <li>
            <span className="font-medium text-white">Hire an external facilitator</span>: Sometimes an outside perspective is needed to objectively assess and navigate the internal dynamics.
          </li>
        </ol>
      </PlaybookCard>

      {/* Facilitate a Leadership Alignment Workshop */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Facilitate a Leadership Alignment Workshop
        </h3>
        <p className="text-gray-300 mb-6">
          Once you have a clearer picture of the issues, bring the leadership team together for a structured workshop aimed at addressing the dysfunctions and forging a path forward.
        </p>

        <h4 className="text-lg font-semibold text-white mb-3">
          Preparation:
        </h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300"><span className="font-medium text-white">Pre-workshop survey</span>: Ask team members to reflect on specific incidents that highlight the dysfunction and suggest topics they feel need to be addressed.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300"><span className="font-medium text-white">Agenda setting</span>: Outline the key discussion points and goals for the workshop, ensuring there's room for open dialogue.</span>
          </li>
        </ul>

        <h4 className="text-lg font-semibold text-white mb-3">
          Workshop Activities (examples):
        </h4>
        <ol className="list-decimal pl-6 space-y-3 text-gray-300 mb-6">
          <li>
            <span className="font-medium text-white">Issue Mapping</span>: Use tools like mind maps or affinity diagrams to visualize the problems and their interconnections.
          </li>
          <li>
            <span className="font-medium text-white">Role Clarification</span>: Revisit the roles and responsibilities of each team member to ensure clarity and alignment.
          </li>
          <li>
            <span className="font-medium text-white">Conflict Resolution Techniques</span>: Introduce and practice techniques such as 'interest-based relational' approach or 'nonviolent communication' to improve how conflicts are managed.
          </li>
          <li>
            <span className="font-medium text-white">Goal Alignment Session</span>: Utilize a method like 'Objectives and Key Results' (OKRs) to align the team's goals and ensure everyone is moving in the same direction.
          </li>
        </ol>
      </PlaybookCard>

      {/* Create Your "Newspaper Headline" */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Create Your "Newspaper Headline"
        </h3>
        <p className="text-gray-300 mb-6">
          Imagine looking at a successful headline about your team a year from now. What does it say? This exercise helps clarify the vision and aspiration of the leadership team as a whole.
        </p>

        <h4 className="text-lg font-semibold text-white mb-3">
          Action Steps:
        </h4>
        <ol className="list-decimal pl-6 space-y-3 text-gray-300 mb-6">
          <li>
            <span className="font-medium text-white">Individual Reflection</span>: Have each member write down their vision of the team's future success.
          </li>
          <li>
            <span className="font-medium text-white">Group Discussion</span>: Share these visions collectively to find common themes and aspirations.
          </li>
          <li>
            <span className="font-medium text-white">Craft the Headline</span>: Together, create a headline that encapsulates the team's future success.
          </li>
        </ol>
      </PlaybookCard>

      {/* Commit to a Timeframe */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Commit to a Timeframe
        </h3>
        <p className="text-gray-300 mb-6">
          Setting a specific timeline for achieving these improvements is crucial. It creates accountability and helps measure progress.
        </p>

        <h4 className="text-lg font-semibold text-white mb-3">
          Implementation:
        </h4>
        <ol className="list-decimal pl-6 space-y-3 text-gray-300 mb-6">
          <li>
            <span className="font-medium text-white">Define Milestones</span>: Break down the headline into actionable milestones with specific deadlines.
          </li>
          <li>
            <span className="font-medium text-white">Regular Check-ins</span>: Schedule monthly or quarterly review sessions to assess progress, discuss challenges, and adjust plans as needed.
          </li>
          <li>
            <span className="font-medium text-white">Celebration of Wins</span>: Recognize and celebrate each milestone to maintain motivation and momentum.
          </li>
        </ol>
      </PlaybookCard>

      {/* Continuous Improvement */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Continuous Improvement
        </h3>
        <p className="text-gray-300 mb-6">
          The process of addressing leadership dysfunction is ongoing. It requires continual efforts to foster open communication, adapt to new challenges, and strengthen team cohesion.
        </p>

        <h4 className="text-lg font-semibold text-white mb-3">
          Ongoing Actions (examples):
        </h4>
        <ol className="list-decimal pl-6 space-y-3 text-gray-300 mb-6">
          <li>
            <span className="font-medium text-white">Feedback Loops</span>: Establish mechanisms for ongoing feedback within the team, encouraging transparency and continuous improvement.
          </li>
          <li>
            <span className="font-medium text-white">Leadership Development</span>: Invest in regular training and development opportunities focused on leadership skills and team management.
          </li>
          <li>
            <span className="font-medium text-white">Annual Retreats</span>: Organise annual retreats to reflect on achievements, renew team bonds, and plan for the future.
          </li>
        </ol>

        <p className="text-gray-300 mt-8">
          By methodically addressing each of these elements, you can transform a dysfunctional leadership team into a cohesive and high-performing group, paving the way for broader organisational success.
        </p>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default WorkingThroughConflictStage3; 